import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight, ShieldCheck, Truck } from 'lucide-react';
import { trackPurchaseConversion } from '../utils/conversionTracking';

const ThankYou = () => {
  const location = useLocation();
  const [txData, setTxData] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const data = {
      type: params.get('type') || 'deposit',
      amount: params.get('amount') || '',
      vehicle: params.get('vehicle') || '',
      transactionId: params.get('txn') || '',
      name: params.get('name') || '',
    };
    setTxData(data);

    // Fire Google Ads purchase conversion with Enhanced Conversion data
    trackPurchaseConversion(
      { fullName: data.name },
      data.transactionId,
      data.amount
    );

    // Fire GA4 purchase event for Google Merchant Center
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'purchase', {
        'transaction_id': data.transactionId,
        'value': parseFloat(data.amount) || 0,
        'currency': 'USD',
        'items': [{
          'item_name': data.vehicle,
          'price': parseFloat(data.amount) || 0,
          'quantity': 1,
        }],
      });
    }
    console.log('Purchase conversion tracked:', data.transactionId);

    // Scroll to top
    window.scrollTo(0, 0);
  }, [location.search]);

  const isFullPurchase = txData.type === 'full' || txData.type === 'purchase';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 overflow-hidden">
        {/* Animated background dots */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Animation */}
            <div className="relative inline-block mb-8">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto animate-pulse-slow">
                <CheckCircle className="w-14 h-14 text-green-600" />
              </div>
              {/* Ring animation */}
              <div
                className="absolute inset-0 w-24 h-24 rounded-full border-4 border-green-400 mx-auto"
                style={{
                  animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) 1',
                  opacity: 0,
                }}
              />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isFullPurchase ? 'Purchase Complete' : 'Deposit Received'}
            </h1>

            <p className="text-xl text-gray-600 mb-2">
              {txData.name ? `${txData.name}, your` : 'Your'} {isFullPurchase ? 'purchase' : 'deposit'} has been processed.
            </p>

            {txData.vehicle && (
              <p className="text-lg font-semibold text-gray-800 mb-2">
                {txData.vehicle}
              </p>
            )}

            {txData.amount && (
              <p className="text-3xl font-bold text-green-600 mb-6">
                ${parseFloat(txData.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            )}

            {txData.transactionId && (
              <p className="text-sm text-gray-400 mb-8">
                Transaction ID: {txData.transactionId}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gray-900 text-white px-8 py-5">
              <h2 className="text-xl font-bold">What Happens Next</h2>
            </div>

            <div className="p-8">
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-700 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Confirmation Email</h3>
                    <p className="text-gray-600">Check your inbox for a receipt and transaction details within a few minutes.</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-700 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Team Contact</h3>
                    <p className="text-gray-600">
                      A member of our team will reach out within 24 hours to {isFullPurchase ? 'arrange pickup or delivery' : 'confirm your reservation and discuss next steps'}.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-700 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {isFullPurchase ? 'Pickup or Delivery' : 'Vehicle Reserved'}
                    </h3>
                    <p className="text-gray-600">
                      {isFullPurchase
                        ? 'We\'ll coordinate a convenient time for you to pick up your vehicle or arrange delivery.'
                        : 'Your vehicle is off the market and reserved exclusively for you until the transaction is finalized.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="mt-8 pt-6 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>Secure payment processed</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <Truck className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span>Delivery available</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>1-Year / 12,000 mi warranty</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Card */}
          <div className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h3 className="font-semibold text-gray-900 text-lg mb-4">Questions? Reach out directly.</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:8173822316"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
              >
                <Phone className="w-5 h-5" />
                (817) 382-2316
              </a>
              <a
                href="mailto:sales@paradise-performance.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-semibold transition-colors"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>
          </div>

          {/* Back to inventory */}
          <div className="mt-8 text-center">
            <Link
              to="/inventory"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors"
            >
              Browse More Vehicles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
